import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Icon, Modal, NotProvided, cardClasses } from '@evogard/ui';
import { businessDate, fromColumn, sum, toColumn } from '@evogard/domain';
import type { Period, StatementBalancePart } from '@evogard/contract';
import { api, ok } from '../lib/api.js';
import { can, useSavePreferences, useSessionContext } from '../lib/session.js';
import { MoneyValue } from './Values.js';

/**
 * A chave do saldo, com a base dentro — o aviso de "o seu pagamento mudou"
 * (`lib/realtime.tsx`) a invalida, e é assim que a baixa do Financeiro zera o
 * número no topo sem ninguém recarregar a página.
 */
export const BALANCE_KEY = ['comercial', 'saldo'] as const;

/**
 * "Meu saldo" — a premiação que a casa deve ao consultor agora, no topo.
 *
 * ## O que o número é
 *
 * A regra é `consultantBalance`, em `@evogard/domain`, decidida pelo dono em
 * 29/09/2026: o lote em aberto soma — o mês que ainda não fechou, a nota que
 * falta enviar, a despesa andando no Financeiro —, e o lote cai para zero
 * quando o Financeiro dá baixa no pagamento. O recusado também sai.
 *
 * **O topo mostra só o que entra NESTE mês** (22/10/2026): as competências
 * anteriores ainda não pagas. A do mês corrente fica de fora, porque é paga no
 * mês seguinte; o Extrato continua com o saldo inteiro.
 *
 * ## Clicar abre a ORIGEM, e cada origem leva ao Extrato (20/10/2026)
 *
 * A referência trazia "Extrato ›" ao lado do valor, e o dono o tirou no dia em
 * que o saldo nasceu: o número era para olhar. Em 20/10/2026 ele pediu o
 * contrário — o saldo de todas as competências, sozinho, não diz de onde vem.
 * Clicar abre os lotes (competência, 1x ou 2x, em que pé está, quanto), e
 * cada lote leva ao Extrato daquela competência com o saldo aberto e o lote
 * marcado (`lot` na busca).
 *
 * ## Quem vê
 *
 * Quem é consultor na base aberta. A rota fica sob `sales.statement:ver`, a
 * permissão do próprio Extrato, e responde `sem_vinculo` a quem não tem
 * vínculo de consultor — e aí o topo não desenha nada, em vez de um
 * "R$ 0,00" que afirmaria um saldo que a pessoa não tem.
 *
 * ## Mora em `components/`, e não em `modules/sales`
 *
 * Quem o desenha é o `Header`, que é da moldura, e a moldura não importa da
 * pasta de um módulo — o mesmo motivo de `StatementClosing.tsx`.
 */
export function BalanceWidget() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const allowed = can(context, 'sales.statement', 'ver');
  /*
   * Antes do `return null` lá embaixo, e não depois: o consultor renderiza
   * primeiro sem a resposta (sai pelo `return`) e depois com ela, e um hook
   * que só existe na segunda derruba o `Header` — e a página junto — com
   * "Rendered more hooks than during the previous render".
   */
  const [open, setOpen] = useState(false);
  /*
   * O olho que oculta o valor (21/10/2026) — para quem abre a tela na frente
   * de um cliente. GRAVADO na preferência da pessoa (`hideBalance`), no
   * servidor e não num `localStorage` (ver o comentário da lateral em
   * `Layout.tsx`): recarregar, trocar de aparelho ou de base mantém a escolha.
   *
   * `pending` é a escolha do clique enquanto o servidor não respondeu — o olho
   * troca na hora, e volta ao que está gravado se a gravação falhar.
   */
  const save = useSavePreferences();
  const [pending, setPending] = useState<boolean | undefined>(undefined);
  const hidden = pending ?? context?.preferences.hideBalance ?? false;
  const toggleHidden = () => {
    const next = !hidden;
    setPending(next);
    save.mutate({ hideBalance: next }, { onSettled: () => setPending(undefined) });
  };

  const query = useQuery({
    queryKey: [...BALANCE_KEY, tenantId],
    enabled: allowed && tenantId !== undefined,
    queryFn: async () => {
      const r = await api.sales.statementBalance();
      return ok(r, 200, 'Não foi possível carregar o saldo.');
    },
    /*
     * Relido de tempos em tempos, e não só no aviso: a parte do mês aberto
     * cresce quando a esteira grava o Extrato, e isso não publica aviso
     * nenhum para o consultor.
     */
    refetchInterval: 5 * 60_000,
  });

  /*
   * Só para quem é consultor na base, e quem diz isso é o servidor. Enquanto
   * ele não respondeu — carregando, ou a consulta falhou —, nada: um esqueleto
   * ou um "indisponível" aqui apareceria para todo colaborador que não é
   * consultor, que é justamente quem não deve ver a peça.
   */
  if (!allowed || query.data?.estado !== 'consultor') return null;

  /*
   * Só o que ele recebe NESTE mês (22/10/2026): as competências anteriores que
   * ainda não foram pagas. A do mês corrente está aberta e só é paga no mês
   * seguinte — somá-la no topo fazia o número parecer o que entra agora. Ela
   * continua no Extrato, que mostra o saldo inteiro.
   */
  const currentMonth = businessDate(new Date()).slice(0, 7);
  const parts = query.data.balanceParts.filter((p) => p.period < currentMonth);
  const balance = toColumn(sum(...parts.map((p) => fromColumn(p.amount)!)));

  return (
    <span className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className="flex h-11 shrink-0 items-center gap-2.5 rounded-borda border border-borda bg-sucesso-suave px-2 transition-colors hover:border-sucesso focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foco sm:pr-3.5 lg:h-[2.125rem]"
      >
        <span className="hidden size-7 place-items-center rounded-borda bg-superficie text-sucesso sm:grid lg:size-6">
          <Icon nome="dinheiro" />
        </span>
        <span className="flex items-baseline gap-2 whitespace-nowrap">
          {/* No telefone só o valor — o rótulo continua para o leitor de tela. */}
          <span className="sr-only text-xs text-texto-suave sm:not-sr-only">
            Meu saldo
          </span>
          <span className="text-sm font-semibold text-texto">
            {hidden ? (
              <span aria-label="saldo oculto">R$ ••••••</span>
            ) : (
              <MoneyValue value={balance} />
            )}
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={toggleHidden}
        aria-pressed={hidden}
        aria-label={hidden ? 'Mostrar o saldo' : 'Ocultar o saldo'}
        title={hidden ? 'Mostrar o saldo' : 'Ocultar o saldo'}
        className="relative grid size-11 shrink-0 place-items-center rounded-borda text-texto-suave transition-colors hover:bg-superficie-suave hover:text-texto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foco lg:size-[2.125rem]"
      >
        <Icon nome="olho" size={20} />
        {/* Um traço por cima do olho quando o valor está oculto — não há ícone de olho fechado. */}
        {hidden && (
          <span
            aria-hidden
            className="absolute h-0.5 w-5 rotate-45 rounded-pilula bg-current"
          />
        )}
      </button>

      {open && (
        <Modal
          open
          onOpenChange={(next) => !next && setOpen(false)}
          title="De onde vem o meu saldo"
          descricao="O que você recebe neste mês: as competências já fechadas que ainda não foram pagas. Cada lote é uma competência e uma categoria (1x ou 2x). Clique num lote para vê-lo no Extrato."
          size="md"
        >
          {parts.length === 0 ? (
            <NotProvided>
              Nada a receber neste mês. A competência em aberto aparece no Extrato.
            </NotProvided>
          ) : (
            <ul className="flex flex-col gap-2">
              {parts.map((part) => (
                <li key={`${part.period}-${part.category}`}>
                  <Link
                    to="/sales/statement"
                    search={{ month: part.period, page: 1, lot: part.category }}
                    onClick={() => setOpen(false)}
                    className={cardClasses({
                      density: 'densa',
                      className:
                        'flex items-center gap-3 transition-colors hover:bg-superficie-suave focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foco',
                    })}
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="font-semibold text-texto">
                        {periodName(part.period)} · {part.category}
                      </span>
                      <span className="text-xs text-texto-suave">
                        {SOURCE_LABEL[part.source]}
                        {part.awards.length > 0 &&
                          ` · ${part.awards.length === 1 ? part.awards[0]!.title : `${part.awards.length} premiações`}`}
                      </span>
                    </span>
                    <span className="font-semibold tabular-nums text-texto">
                      <MoneyValue value={part.amount} />
                    </span>
                    <Icon nome="seta-direita" size={16} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </span>
  );
}

/** Em que pé está o lote, curto — a frase inteira mora no Extrato. */
// @valor — as chaves são os valores de `BALANCE_PART_SOURCES`
const SOURCE_LABEL: Readonly<Record<StatementBalancePart['source'], string>> = {
  competencia_aberta: 'Competência aberta, ainda pode crescer',
  aguardando_nota: 'Aguardando a nota fiscal',
  no_financeiro: 'Nota enviada, no Financeiro',
};

/** `2026-09` → "setembro de 2026". UTC pelo motivo de `periodLabel` do comercial. */
function periodName(period: Period): string {
  return new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${period}-01T00:00:00Z`));
}
